import { PROJECT_CONSTANTS, USER_MGMT_CNST } from '../constants/proj.cnst';

export const WRITE_OPERATIONS = [
  USER_MGMT_CNST.USER_MODULE_OPERATIONS.ADD_USER,
  USER_MGMT_CNST.USER_MODULE_OPERATIONS.UPDATE_USER,
  USER_MGMT_CNST.ROLE_MODULE_OPERATIONS.ADD_ROLE,
  USER_MGMT_CNST.ROLE_MODULE_OPERATIONS.UPDATE_ROLE,
  USER_MGMT_CNST.CUSTOMER_MODULE_OPERATIONS.ADD_CUSTOMER,
  USER_MGMT_CNST.CUSTOMER_MODULE_OPERATIONS.UPDATE_CUSTOMER
];

export function parseUserPermissions(raw: any): Record<string, any> {
  if (!raw) {
    return {};
  }

  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return {};
    }
    return parsed;
  } catch {
    return {};
  }
}

export function readLocalUserPermissions(): Record<string, any> {
  if (typeof localStorage === 'undefined') {
    return {};
  }

  try {
    return parseUserPermissions(localStorage.getItem('userPermissions'));
  } catch {
    return {};
  }
}

export function resolveUserPermissions(permissions?: any): Record<string, any> {
  const fromInput = parseUserPermissions(permissions);
  if (Object.keys(fromInput).length > 0) {
    return fromInput;
  }
  return readLocalUserPermissions();
}

export function hasApplicationAccess(permissions: any, applicationName: string): boolean {
  if (!applicationName) {
    return false;
  }
  const parsed = parseUserPermissions(permissions);
  return !!parsed[applicationName];
}

export function getApplicationResources(permissions: any, applicationName: string): Record<string, any> {
  const parsed = parseUserPermissions(permissions);
  const resources = parsed?.[applicationName]?.resources;
  if (!resources || typeof resources !== 'object' || Array.isArray(resources)) {
    return {};
  }
  return resources;
}

export function hasResourceAccess(permissions: any, resourceKey: string, applicationName: string): boolean {
  if (!resourceKey) {
    return false;
  }
  const resources = getApplicationResources(permissions, applicationName);
  return !!resources[resourceKey];
}

export function getResourceActions(permissions: any, resourceKey: string, applicationName: string): string[] {
  if (!resourceKey) {
    return [];
  }
  const resources = getApplicationResources(permissions, applicationName);
  const actions = resources?.[resourceKey]?.actions;
  return Array.isArray(actions) ? actions : [];
}

export function hasAction(
  permissions: any,
  resourceKey: string,
  action: string,
  applicationName: string
): boolean {
  if (!action) {
    return false;
  }
  const normalizedAction = String(action).trim().toUpperCase();
  return getResourceActions(permissions, resourceKey, applicationName).some(
    (item) => String(item).trim().toUpperCase() === normalizedAction
  );
}

export function hasReadPermission(permissions: any, resourceKey: string, applicationName: string): boolean {
  return hasAction(permissions, resourceKey, USER_MGMT_CNST.USER_PERMISSION_ACTION.READ, applicationName);
}

export function hasEditPermission(permissions: any, resourceKey: string, applicationName: string): boolean {
  return hasAction(permissions, resourceKey, USER_MGMT_CNST.USER_PERMISSION_ACTION.EDIT, applicationName);
}

export function isWriteOperation(operation: string | null | undefined): boolean {
  const normalized = String(operation || '').toLowerCase();
  return WRITE_OPERATIONS.includes(normalized);
}

export function getUserManagementAppName(): string {
  return PROJECT_CONSTANTS.USERMGMT_APP_NAME;
}

export function getApcrdaAppName(): string {
  return PROJECT_CONSTANTS.APPLICATION_NAME;
}
